const STANDARD_DASHBOARD_INTENDED_USE_STATEMENT =
  "The pDx Dashboard is intended for healthcare professionals to access, review, and visualize health data and biomarker results (HR, RR, TV, FEV1, FVC, PEF, and FEV1/FVC) collected or generated through the pDx Platform. It supports professional review and evaluation of these data but does not independently provide a diagnosis, prescribe treatment, or replace professional clinical judgment.";
const CDS_DASHBOARD_INTENDED_USE_STATEMENT =
  "The purpleDx CDS Dashboard is intended for healthcare professionals to access, review, and visualize patient health data, biomarker results, and software-generated clinical decision support information provided by the purpleDx CDS variant. It supports clinical assessment but does not replace professional clinical judgment, independently establish a diagnosis, or autonomously select or prescribe treatment.";
const CLINICAL_DASHBOARD_INTENDED_USE_STATEMENT =
  "The purpleDx Dashboard is intended for healthcare professionals to access, review, and visualize health data and biomarker results (HR, RR, TV, FEV1, FVC, PEF, and FEV1/FVC) collected or generated through the purpleDx platform. It supports professional review and evaluation of these data but does not independently provide a diagnosis, prescribe treatment, or replace professional clinical judgment.";
const RESEARCH_DASHBOARD_INTENDED_USE_STATEMENT =
  "The pDx Dashboard is intended for research purposes only. It enables researchers and professionals to access, review, and visualize health-related data and biomarker results, including HR, RR, TV, FEV1, FVC, PEF, and FEV1/FVC, collected or generated through the pDx Platform. The dashboard is not intended for clinical use and does not perform or support diagnosis, treatment, or clinical decision-making. It is not currently intended to be used as a Software as a Medical Device (SaMD).";

export const PRODUCT_PRESENTATION_PROFILE_LABELLING_FALLBACKS = Object.freeze({
  "uk-ce-class-iia": Object.freeze({
    branding: Object.freeze({
      appName: "pDx",
      logoAsset: "pdx_logo.png",
    }),
    labelling: Object.freeze({
      exclusivelyForClinicalInvestigations: true,
      dashboard: Object.freeze({
        intendedUseStatement: STANDARD_DASHBOARD_INTENDED_USE_STATEMENT,
      }),
    }),
  }),
  "eu-ce-class-iia": Object.freeze({
    branding: Object.freeze({
      appName: "pDx",
      logoAsset: "pdx_logo.png",
    }),
    labelling: Object.freeze({
      exclusivelyForClinicalInvestigations: true,
      dashboard: Object.freeze({
        intendedUseStatement: STANDARD_DASHBOARD_INTENDED_USE_STATEMENT,
      }),
    }),
  }),
  "jp-research": Object.freeze({
    branding: Object.freeze({
      appName: "pDx",
      logoAsset: "pdx_logo.png",
    }),
    labelling: Object.freeze({
      exclusivelyForClinicalInvestigations: true,
      dashboard: Object.freeze({
        intendedUseStatement: STANDARD_DASHBOARD_INTENDED_USE_STATEMENT,
      }),
    }),
  }),
  "us-cds": Object.freeze({
    branding: Object.freeze({
      appName: "purpleDx CDS",
      logoAsset: "purpledx_primary_logo.png",
    }),
    labelling: Object.freeze({
      exclusivelyForClinicalInvestigations: true,
      dashboard: Object.freeze({
        intendedUseStatement: CDS_DASHBOARD_INTENDED_USE_STATEMENT,
      }),
    }),
  }),
  "us-510k-denovo": Object.freeze({
    branding: Object.freeze({
      appName: "purpleDx",
      logoAsset: "purpledx_primary_logo.png",
    }),
    labelling: Object.freeze({
      exclusivelyForClinicalInvestigations: true,
      dashboard: Object.freeze({
        intendedUseStatement: CLINICAL_DASHBOARD_INTENDED_USE_STATEMENT,
      }),
    }),
  }),
  "au-research": Object.freeze({
    branding: Object.freeze({
      appName: "pDx",
      logoAsset: "pdx_logo.png",
    }),
    labelling: Object.freeze({
      exclusivelyForClinicalInvestigations: false,
      dashboard: Object.freeze({
        intendedUseStatement: RESEARCH_DASHBOARD_INTENDED_USE_STATEMENT,
      }),
    }),
  }),
  "uk-demo": Object.freeze({
    branding: Object.freeze({
      appName: "pDx Demo",
      logoAsset: "pdx_logo.png",
    }),
    labelling: Object.freeze({
      exclusivelyForClinicalInvestigations: true,
      dashboard: Object.freeze({
        intendedUseStatement: STANDARD_DASHBOARD_INTENDED_USE_STATEMENT,
      }),
    }),
  }),
});

function normalizeProfileId(profileId) {
  if (typeof profileId !== "string") {
    return null;
  }

  const trimmed = profileId.trim().toLowerCase();
  return trimmed || null;
}

function shouldPreferFallbackProfile(productPresentationProfile, fallbackProfileId) {
  const normalizedFallbackId = normalizeProfileId(fallbackProfileId);

  if (!normalizedFallbackId) {
    return false;
  }

  const normalizedLiveProfileId = normalizeProfileId(productPresentationProfile?.id);
  return normalizedLiveProfileId !== normalizedFallbackId;
}

function pickFirstString(...values) {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return null;
}

function pickFirstBoolean(...values) {
  for (const value of values) {
    if (typeof value === "boolean") {
      return value;
    }
  }

  return null;
}

export function resolveFallbackProductPresentationLabelling(profileId) {
  const normalizedProfileId = normalizeProfileId(profileId);
  if (!normalizedProfileId) {
    return null;
  }

  return (
    PRODUCT_PRESENTATION_PROFILE_LABELLING_FALLBACKS[normalizedProfileId] ||
    null
  );
}

export function resolveProductPresentationLabelling(
  productPresentationProfile,
  {
    surfaceKey = "dashboard",
    fallbackProfileId = null,
    fallbackAppName = "pDx",
    fallbackLogoAsset = "pdx_logo.png",
  } = {},
) {
  const fallbackProfile =
    resolveFallbackProductPresentationLabelling(fallbackProfileId);
  const effectiveLiveProfile = shouldPreferFallbackProfile(
    productPresentationProfile,
    fallbackProfileId,
  )
    ? null
    : productPresentationProfile;
  const liveSurfaceLabelling =
    effectiveLiveProfile?.labelling?.[surfaceKey] || null;
  const fallbackSurfaceLabelling =
    fallbackProfile?.labelling?.[surfaceKey] || null;

  return {
    appName: pickFirstString(
      effectiveLiveProfile?.branding?.appName,
      fallbackProfile?.branding?.appName,
      fallbackAppName,
    ),
    logoAsset: pickFirstString(
      effectiveLiveProfile?.branding?.logoAsset,
      fallbackProfile?.branding?.logoAsset,
      fallbackLogoAsset,
    ),
    intendedUseStatement: pickFirstString(
      liveSurfaceLabelling?.intendedUseStatement,
      fallbackSurfaceLabelling?.intendedUseStatement,
    ),
    exclusivelyForClinicalInvestigations: pickFirstBoolean(
      effectiveLiveProfile?.labelling
        ?.exclusivelyForClinicalInvestigations,
      fallbackProfile?.labelling?.exclusivelyForClinicalInvestigations,
    ),
  };
}
